import React, { useEffect, useRef } from 'react';
import SignaturePad from 'react-signature-canvas';
import { useIsRequiredElement } from '../../../hook/useIsRequiredElement';
import ElementErrorMessage from './components/element-error-message';
import { GridYUnit, GridYValue } from '../../../constants';

export const SignatureElement = function FromElement({
  element,
  handlerChange,
  error,
  formHasOnlyOneSubmitter,
  scale,
  submitterType,
  isPrint,
}) {
  const isRequiredElement = useIsRequiredElement(element, formHasOnlyOneSubmitter, submitterType);
  const sigPad = useRef();

  useEffect(() => {
    const canvas = sigPad.current?.getCanvas();
    if (!canvas) return undefined;

    // The desktop layout renders the form inside a `transform: scale(...)`
    // wrapper, so the bitmap has to match the visual size for the points
    // signature_pad reads off getBoundingClientRect() to line up. The mobile
    // layout is not transformed and passes no scale at all — without the
    // fallback the bitmap ends up 0x0 and nothing can be drawn.
    const canvasScale = scale || 1;
    let isSized = false;

    const syncCanvasSize = () => {
      const width = Math.round(canvas.offsetWidth * canvasScale);
      const height = Math.round(canvas.offsetHeight * canvasScale);

      if (!width || !height || (isSized && width === canvas.width && height === canvas.height)) {
        return;
      }

      // Resizing the canvas wipes it, so keep whatever is already drawn.
      const drawn = isSized && !sigPad.current.isEmpty() ? sigPad.current.toDataURL() : element.options.valueInput;

      canvas.width = width;
      canvas.height = height;
      isSized = true;

      if (drawn) {
        // fromDataURL() sizes the image by devicePixelRatio by default, which
        // squeezes it into the top-left corner on retina phones.
        sigPad.current.fromDataURL(drawn, { width, height });
      }
    };

    syncCanvasSize();

    // Keeps the bitmap in sync when the field is resized, e.g. on rotation.
    const resizeObserver = new ResizeObserver(syncCanvasSize);
    resizeObserver.observe(canvas);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line
  }, [scale]);

  const onDrawEnd = () => {
    if (sigPad.current) {
      handlerChange(element, sigPad.current.toDataURL());
    }
  };

  const Clear = () => {
    sigPad.current.clear();
  };

  const getCanvasHeight = (size) => {
    if (size === 'small') {
      return '105px';
    } else if (size == 'medium') {
      return '70px';
    } else if (size == 'large') {
      return '53px';
    }

    return '105px';
  };

  return (
    <div style={{ pageBreakInside: 'avoid' }}>
      <label
        htmlFor={element.id}
        className="form-label"
        style={{ zIndex: 5000 + (element.options.zIndex || 10), width: 'fit-content', position: 'relative' }}
      >
        {element.options.label} <span className="required">{isRequiredElement ? '*' : ''}</span>
      </label>
      <div
        className={`sigCanvas-wrapper sigCanvas-${element.options.size}`}
        style={{
          width: '100%',
          height: element.options.innerElemHeight * GridYValue + GridYUnit,
          zIndex: 5000 + (element.options.zIndex || 10),
          position: 'relative',
        }}
      >
        <SignaturePad
          className={`${element.options.readOnly ? 'readonly' : ''}`}
          ref={sigPad}
          penColor="black"
          clearOnResize={false}
          canvasProps={{ className: `sigCanvas` }}
          onEnd={onDrawEnd}
        />
      </div>

      <span
        className={`sigCanvas-clear ${element.options.readOnly ? 'readonly' : ''}`}
        onClick={Clear}
        style={{ zIndex: 5000 + (element.options.zIndex || 10), width: 'fit-content', position: 'relative' }}
      >
        Clear
      </span>

      <ElementErrorMessage message={error} />
      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}
    </div>
  );
};
